import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { directusOperations, getCustomersImproved, searchCustomersImproved, getCustomersSortedByTransactionSchema, getAllCustomersSortedAlphabetically, searchCustomersForInteractions, searchCustomersForMapsView, getCustomersFromInteractionTable, getCustomersFromTambakByPIC, getCustomersWithCompletedInteractions, getCustomersWithPendingInteractions } from '../../lib/directus';
import { Customer, CustomersState, CreateCustomerData } from '../../lib/types';
import { getWIBTimeForDirectus } from '../../lib/timeUtils';

// Helper function to transform Customer_Tambak data to Customer interface
const transformCustomerData = (rawCustomer: any): Customer => {
  // Generate numeric ID from No_pelanggan for compatibility
  const generateIdFromNoPelanggan = (noPelanggan: string): number => {
    if (!noPelanggan) return 0;
    // Convert string like "A-0001" to numeric ID
    // Remove letters and hyphens, convert to number
    const numericPart = noPelanggan.replace(/[^\d]/g, '');
    return parseInt(numericPart) || 0;
  };

  // Helper function to format user name from user object or ID
  const formatUserName = (user: any): string => {
    if (!user) return '';
    if (typeof user === 'string') return user; // Fallback for string IDs
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || user.id || '';
  };

  // Helper function to format date for display
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Belum ada';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString; // Return as-is if parsing fails
    }
  };

  // Helper function to extract coordinates from various possible fields
  const extractCoordinates = (customer: any) => {
    // Try to extract coordinates from different possible field structures
    let lat = null;
    let lng = null;

    // Check for coordinates object (JSON field)
    if (customer.coordinates) {
      if (typeof customer.coordinates === 'string') {
        try {
          const parsed = JSON.parse(customer.coordinates);
          lat = parsed.lat;
          lng = parsed.lng;
        } catch {
          console.warn('Failed to parse coordinates JSON:', customer.coordinates);
        }
      } else if (customer.coordinates.lat && customer.coordinates.lng) {
        lat = customer.coordinates.lat;
        lng = customer.coordinates.lng;
      }
    }

    // Check for direct latitude/longitude fields
    if (!lat && customer.latitude) lat = customer.latitude;
    if (!lng && customer.longitude) lng = customer.longitude;

    // Check for Directus-style coordinate fields
    if (!lat && customer.Latitude) lat = customer.Latitude;
    if (!lng && customer.Longitude) lng = customer.Longitude;

    // Validate coordinates are valid numbers and not zero
    const validLat = typeof lat === 'number' && !isNaN(lat) && lat !== 0;
    const validLng = typeof lng === 'number' && !isNaN(lng) && lng !== 0;

    return {
      latitude: validLat ? lat : undefined,
      longitude: validLng ? lng : undefined,
      coordinates: (validLat && validLng) ? { lat, lng } : undefined
    };
  };

  const coordData = extractCoordinates(rawCustomer);

  return {
    ...rawCustomer,
    // Use generated ID if real id is not available
    id: rawCustomer.id || generateIdFromNoPelanggan(rawCustomer.No_pelanggan),
    
    // Support both Data_Customer and Customer_Tambak field names
    NamaPelangganTambak: rawCustomer.Nama_Pelanggan_Tambak || rawCustomer.Nama_Pelanggan || '', 
    name: rawCustomer.Nama_Pelanggan_Tambak || rawCustomer.Nama_Pelanggan || '',
    idcust: rawCustomer.No_pelanggan || '',
    address: rawCustomer.Alamat || '',
    contact_person: Array.isArray(rawCustomer.PIC) 
      ? rawCustomer.PIC.map(formatUserName).join(', ') 
      : formatUserName(rawCustomer.PIC),
    phone: rawCustomer.Kontak || rawCustomer.Contacs || '',
    tipe_pelanggan: rawCustomer.Tipe_Pelanggan || '',
    priority: 'medium' as const,
    status: 'active' as const,
    farmIsActive: rawCustomer.farmIsActive !== undefined ? rawCustomer.farmIsActive : true,
    last_visit: rawCustomer.tglInteraksiTerakhir || '',
    created_at: rawCustomer.created_at || getWIBTimeForDirectus(),
    updated_at: rawCustomer.updated_at || getWIBTimeForDirectus(),
    
    // NEW: Approval workflow fields
    approval_status: rawCustomer.approval_status || 'approved', // Default to approved for existing customers
    approval_date: rawCustomer.approval_date,
    approved_by: formatUserName(rawCustomer.approved_by), // Format user object to string
    approval_notes: rawCustomer.approval_notes,
    
    // Enhanced Customer_Tambak information for detailed view
    tambak_name: rawCustomer.Nama_Pelanggan_Tambak || rawCustomer.Nama_Pelanggan,
    tambak_size: rawCustomer.ukuranTambak,
    year_built: rawCustomer.tahunDibangun,
    last_purchase_date: rawCustomer.tglPembelianTerakhir,
    lead_source: rawCustomer.source,
    assigned_pic: formatUserName(rawCustomer.PIC),
    pic_id: typeof rawCustomer.PIC === 'object' ? rawCustomer.PIC?.id || '' : String(rawCustomer.PIC || ''),
    
    // Coordinate data for map display
    latitude: coordData.latitude,
    longitude: coordData.longitude,
    coordinates: coordData.coordinates,
    
    // Computed fields for backward compatibility  
    distance: '-', // Default distance when no location available
    lastInteraction: formatDate(rawCustomer.tglInteraksiTerakhir),
    revenue: 'Rp 0' // Default revenue
  };
};

// Async thunks for API calls
export const fetchCustomersLimited = createAsyncThunk(
  'customers/fetchCustomersLimited',
  async (limit = 20, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { customers: CustomersState };
      const now = Date.now();
      const lastFetched = state.customers.customersLastFetched;
      const cacheTTL = state.customers.customersCacheTTL;
      
      // Check cache for dashboard optimization
      if (lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          // Return cached data by rejecting with special flag
          return rejectWithValue({ cached: true, message: 'Using cached customers data' });
        }
      }
      
      // Fetch limited customers for dashboard
      const customers = await directusOperations.getCustomers();
      return (customers || []).slice(0, limit).map(transformCustomerData); // Return only first N customers
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers');
    }
  }
);

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const customers = await directusOperations.getCustomers();
      return customers.map(transformCustomerData);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers');
    }
  }
);

// New improved customer fetching using Data_Customer as primary source
export const fetchCustomersImproved = createAsyncThunk(
  'customers/fetchCustomersImproved',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching customers using improved Data_Customer strategy');
      const customers = await getCustomersImproved();
      console.log('✅ Improved fetch successful, got', customers.length, 'customers');
      return customers.map(transformCustomerData);
    } catch (error) {
      console.error('❌ Improved fetch failed, falling back to legacy method:', error);
      try {
        // Fallback to legacy method
        const customers = await directusOperations.getCustomers();
        return customers.map(transformCustomerData);
      } catch (fallbackError) {
        return rejectWithValue(fallbackError instanceof Error ? fallbackError.message : 'Failed to fetch customers with both methods');
      }
    }
  }
);

export const searchCustomers = createAsyncThunk(
  'customers/searchCustomers',
  async (params: { 
    query: string; 
    page?: number; 
    limit?: number; 
    append?: boolean; // For infinite scroll
  }, { rejectWithValue }) => {
    const { query, page = 1, limit = 20, append = false } = params;
    console.log('🔍 Redux searchCustomers called with:', { query, page, limit, append });
    
    try {
      console.log('📊 Searching customers with pagination');
      const result = await directusOperations.searchCustomers(query, { page, limit });
      console.log('✅ Search successful, got', result.data.length, 'customers');
      
      return {
        customers: result.data.map(transformCustomerData),
        pagination: {
          page: result.page,
          limit: result.limit,
          offset: result.offset,
          hasMore: result.hasMore
        },
        append,
        query
      };
    } catch (error) {
      console.error('❌ Redux searchCustomers failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to search customers');
    }
  }
);

// New improved search using Data_Customer as primary source
export const searchCustomersWithImprovedMethod = createAsyncThunk(
  'customers/searchCustomersWithImprovedMethod',
  async (params: { 
    query: string; 
    page?: number; 
    limit?: number; 
    append?: boolean; // For infinite scroll
  }, { rejectWithValue }) => {
    const { query, page = 1, limit = 20, append = false } = params;
    console.log('🔍 Redux searchCustomersWithImprovedMethod called with:', { query, page, limit, append });
    
    try {
      console.log('📊 Searching customers using improved Data_Customer strategy');
      
      // DEBUG removed after successful field mapping fix
      
      const result = await searchCustomersImproved(query, { page, limit });
      console.log('✅ Improved search successful, got', result.data.length, 'customers');
      console.log('🔍 REDUX - Using Data_Customer primary method - sample data:', result.data[0]);
      
      return {
        customers: result.data.map(transformCustomerData),
        pagination: {
          page: result.page,
          limit: result.limit,
          offset: result.offset,
          hasMore: result.hasMore
        },
        append,
        query
      };
    } catch (error) {
      console.error('❌ Improved search failed, falling back to legacy method:', error);
      try {
        // Fallback to legacy method
        console.log('⚠️ FALLBACK - Using Customer_Tambak legacy method');
        const result = await directusOperations.searchCustomers(query, { page, limit });
        console.log('🔍 FALLBACK - Sample data structure:', result.data[0]);
        return {
          customers: result.data.map(transformCustomerData),
          pagination: {
            page: result.page,
            limit: result.limit,
            offset: result.offset,
            hasMore: result.hasMore
          },
          append,
          query
        };
      } catch (fallbackError) {
        console.error('❌ Both improved and legacy search failed:', fallbackError);
        return rejectWithValue(fallbackError instanceof Error ? fallbackError.message : 'Failed to search customers with both methods');
      }
    }
  }
);

// NEW: Comprehensive search for interaction dropdowns (includes ALL customer types)
export const searchCustomersForInteractionDropdowns = createAsyncThunk(
  'customers/searchCustomersForInteractionDropdowns',
  async (params: { 
    query: string; 
    page?: number; 
    limit?: number; 
    append?: boolean; // For infinite scroll
  }, { rejectWithValue }) => {
    const { query, page = 1, limit = 20, append = false } = params;
    console.log('🔍 Redux searchCustomersForInteractionDropdowns called with:', { query, page, limit, append });
    
    try {
      console.log('📊 Searching customers using comprehensive interaction search (ALL customer types)');
      
      const result = await searchCustomersForInteractions(query, { page, limit });
      console.log('✅ Comprehensive interaction search successful, got', result.data.length, 'customers');
      console.log('🎯 Customer types found:', [...new Set(result.data.map((c: any) => c.tipe_pelanggan).filter(Boolean))]);
      
      return {
        customers: result.data.map(transformCustomerData),
        pagination: {
          page: result.page,
          limit: result.limit,
          offset: result.offset,
          hasMore: result.hasMore
        },
        append,
        query
      };
    } catch (error) {
      console.error('❌ Redux searchCustomersForInteractionDropdowns failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to search customers for interaction dropdowns');
    }
  }
);

export const loadMoreCustomers = createAsyncThunk(
  'customers/loadMoreCustomers',
  async (params: { query: string; currentPage: number; limit?: number }, { rejectWithValue }) => {
    const { query, currentPage, limit = 20 } = params;
    const nextPage = currentPage + 1;
    
    console.log('📖 Loading more customers, page:', nextPage);
    
    try {
      const result = await directusOperations.searchCustomers(query, { 
        page: nextPage, 
        limit 
      });
      
      console.log('✅ Load more successful, got', result.data.length, 'customers');
      
      return {
        customers: result.data.map(transformCustomerData),
        pagination: {
          page: result.page,
          limit: result.limit,
          offset: result.offset,
          hasMore: result.hasMore
        }
      };
    } catch (error) {
      console.error('❌ Load more failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load more customers');
    }
  }
);

export const fetchNearbyCustomers = createAsyncThunk(
  'customers/fetchNearbyCustomers',
  async ({ latitude, longitude, radius = 5 }: { latitude: number; longitude: number; radius?: number }, { rejectWithValue }) => {
    try {
      const customers = await directusOperations.getNearbyCustomers(latitude, longitude, radius);
      return customers.map(transformCustomerData);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch nearby customers');
    }
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (customerId: string, { rejectWithValue }) => {
    try {
      const customer = await directusOperations.getCustomerById(customerId);
      return customer ? transformCustomerData(customer) : null;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer');
    }
  }
);

export const fetchCustomerStats = createAsyncThunk(
  'customers/fetchCustomerStats',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getCustomerStats();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer stats');
    }
  }
);

export const fetchCustomerCount = createAsyncThunk(
  'customers/fetchCustomerCount',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getCustomerCount();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer count');
    }
  }
);

export const fetchTotalCustomerCount = createAsyncThunk(
  'customers/fetchTotalCustomerCount',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getTotalCustomerCount();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch total customer count');
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData: CreateCustomerData, { rejectWithValue }) => {
    try {
      const newCustomer = await directusOperations.createCustomer(customerData);
      return transformCustomerData(newCustomer);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create customer');
    }
  }
);

// Fetch customer interaction data for sorting purposes
export const fetchCustomerInteractions = createAsyncThunk(
  'customers/fetchCustomerInteractions',
  async (userId?: string, { rejectWithValue }) => {
    try {
      // Use existing method to get user's customer interactions
      // This will automatically filter by current user's PIC field
      const interactions = await directusOperations.getUserCustomerInteractions(1000); // Get more interactions for sorting
      
      console.log('🔍 fetchCustomerInteractions - Loaded', interactions?.length || 0, 'user-specific interactions for Last Interaction sorting');
      
      return interactions || [];
    } catch (error) {
      console.warn('Failed to fetch customer interactions for sorting:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer interactions');
    }
  }
);

// Fetch customer transaction data for sorting purposes with smart caching
export const fetchCustomerTransactions = createAsyncThunk(
  'customers/fetchCustomerTransactions',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { customers: CustomersState };
      const lastFetched = state.customers.transactionsLastFetched;
      
      // Check if we have recent data (within last 5 minutes) and don't force refresh
      if (!forceRefresh && lastFetched) {
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        const lastFetchedTime = new Date(lastFetched).getTime();
        
        if (lastFetchedTime > fiveMinutesAgo) {
          console.log('🚀 Using cached transaction data (fetched', Math.round((Date.now() - lastFetchedTime) / 1000), 'seconds ago)');
          return state.customers.customerTransactions;
        }
      }
      
      console.log('🔄 Fetching latest customer transaction data for sorting');
      const transactions = await directusOperations.getLatestCustomerTransactions();
      
      console.log('✅ Latest transaction data fetched successfully:', transactions?.length || 0, 'records');
      return transactions || [];
    } catch (error) {
      console.error('❌ Failed to fetch customer transactions for sorting:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer transactions');
    }
  }
);

// NEW: Schema-compliant customer fetching based on transaction sorting
export const fetchCustomersByTransactionSchema = createAsyncThunk(
  'customers/fetchCustomersByTransactionSchema',
  async (sortOrder: 'asc' | 'desc' = 'desc', { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching customers using schema-compliant transaction sorting:', sortOrder);
      const customers = await getCustomersSortedByTransactionSchema(sortOrder);
      console.log('✅ Schema-compliant fetch successful, got', customers.length, 'customers');
      return {
        customers: customers.map(transformCustomerData),
        sortOrder,
        schemaCompliant: true
      };
    } catch (error) {
      console.error('❌ Schema-compliant fetch failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers with schema-compliant transaction sorting');
    }
  }
);

// NEW: Data_Customer-based alphabetical sorting
export const fetchCustomersAlphabetically = createAsyncThunk(
  'customers/fetchCustomersAlphabetically',
  async (sortOrder: 'asc' | 'desc' = 'asc', { rejectWithValue }) => {
    try {
      console.log('🔤 Fetching customers using Data_Customer alphabetical sorting:', sortOrder);
      const customers = await getAllCustomersSortedAlphabetically(sortOrder);
      console.log('✅ Alphabetical sort successful, got', customers.length, 'customers');
      return {
        customers: customers.map(transformCustomerData),
        sortOrder,
        alphabeticalSort: true
      };
    } catch (error) {
      console.error('❌ Alphabetical sort failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers with Data_Customer alphabetical sorting');
    }
  }
);

// Load all customers for map view with coordinates from both tables
export const fetchAllCustomersForMap = createAsyncThunk(
  'customers/fetchAllCustomersForMap',
  async (_, { rejectWithValue }) => {
    console.log('🗺️ Loading customers with coordinates from both Data_Customer and Customer_Tambak tables');
    
    try {
      // Import the new dual-table function
      const { getCustomersForMap } = await import('../../lib/directus');
      
      // Use the new function that queries both tables with deduplication
      const customers = await getCustomersForMap();
      console.log('✅ Map data loaded successfully, got', customers.length, 'customers with coordinates');
      return {
        customers: customers.map(transformCustomerData),
        allDataLoaded: true
      };
    } catch (error) {
      console.error('❌ Failed to load customers for map:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load customers for map');
    }
  }
);

// Fetch unique customer types for filtering
export const fetchUniqueCustomerTypes = createAsyncThunk(
  'customers/fetchUniqueCustomerTypes',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🏷️ Fetching unique customer types for filter dropdown');
      const { getUniqueCustomerTypes } = await import('../../lib/directus');
      const customerTypes = await getUniqueCustomerTypes();
      console.log(`✅ Successfully loaded ${customerTypes.length} customer type options`);
      return customerTypes;
    } catch (error) {
      console.error('❌ Failed to fetch unique customer types:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load customer types');
    }
  }
);

// Comprehensive search for maps view with phone and PIC lookup
export const searchCustomersForMaps = createAsyncThunk(
  'customers/searchCustomersForMaps',
  async (params: { query: string; customerType?: string; limit?: number }, { rejectWithValue }) => {
    try {
      const { query, customerType = 'all', limit = -1 } = params;
      console.log('🗺️ Redux searchCustomersForMaps called with:', { query, customerType, limit });

      const result = await searchCustomersForMapsView(query, { customerType, limit });
      console.log(`✅ Maps search found ${result.mapReady} customers ready for map view`);

      return {
        data: result.data,
        totalFound: result.totalFound,
        mapReady: result.mapReady,
        hasPhoneMatches: result.hasPhoneMatches,
        searchInfo: result.searchInfo
      };
    } catch (error) {
      console.error('❌ Redux searchCustomersForMaps failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Maps search failed');
    }
  }
);

// 🎯 NEW: Fetch customers with interactions for current user (using dedicated function)
export const fetchCustomersWithInteractions = createAsyncThunk(
  'customers/fetchCustomersWithInteractions',
  async (params: {
    limit?: number;
    offset?: number;
    includeHidden?: boolean;
    includeDone?: boolean; // Include completed interactions
    includePending?: boolean; // Include pending interactions
  } = {}, { rejectWithValue }) => {
    try {
      const {
        limit = -1,
        offset = 0,
        includeHidden = false,
        includeDone = true,
        includePending = true
      } = params;
      console.log('═══════════════════════════════════════════════════════');
      console.log('🎯 Redux: Fetching customers from customer_Interaction table');
      console.log('Parameters:', { limit, offset, includeHidden, includeDone, includePending });
      console.log('═══════════════════════════════════════════════════════');

      // Use the new dedicated function with status filters
      const customers = await getCustomersFromInteractionTable({
        limit,
        offset,
        includeHidden,
        includeDone,
        includePending
      });

      console.log('═══════════════════════════════════════════════════════');
      console.log(`✅ Redux: Successfully fetched ${customers.length} customers from customer_Interaction`);
      console.log('═══════════════════════════════════════════════════════');

      return customers.map(transformCustomerData);
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ Redux: Failed to fetch customers with interactions');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers with interactions');
    }
  }
);

// ✅ NEW: Fetch customers with COMPLETED interactions only (done = true)
export const fetchCustomersWithCompletedInteractions = createAsyncThunk(
  'customers/fetchCustomersWithCompletedInteractions',
  async (params: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const { limit = -1, offset = 0 } = params;
      console.log('═══════════════════════════════════════════════════════');
      console.log('✅ Redux: Fetching customers with COMPLETED interactions (done = true) only');
      console.log('Parameters:', { limit, offset });
      console.log('═══════════════════════════════════════════════════════');

      // ✅ Use dedicated function for completed interactions (includes PIC filtering automatically)
      const customers = await getCustomersWithCompletedInteractions({
        limit,
        offset
      });

      console.log('═══════════════════════════════════════════════════════');
      console.log(`✅ Redux: Successfully fetched ${customers.length} customers with COMPLETED interactions`);
      console.log('═══════════════════════════════════════════════════════');

      return customers.map(transformCustomerData);
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ Redux: Failed to fetch customers with completed interactions');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers with completed interactions');
    }
  }
);

// ⏳ NEW: Fetch customers with PENDING interactions only (done = false)
export const fetchCustomersWithPendingInteractions = createAsyncThunk(
  'customers/fetchCustomersWithPendingInteractions',
  async (params: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const { limit = -1, offset = 0 } = params;
      console.log('═══════════════════════════════════════════════════════');
      console.log('⏳ Redux: Fetching customers with PENDING interactions (done = false) only');
      console.log('Parameters:', { limit, offset });
      console.log('═══════════════════════════════════════════════════════');

      // ⏳ Use dedicated function for pending interactions (includes PIC filtering automatically)
      const customers = await getCustomersWithPendingInteractions({
        limit,
        offset
      });

      console.log('═══════════════════════════════════════════════════════');
      console.log(`✅ Redux: Successfully fetched ${customers.length} customers with PENDING interactions`);
      console.log('═══════════════════════════════════════════════════════');

      return customers.map(transformCustomerData);
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ Redux: Failed to fetch customers with pending interactions');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers with pending interactions');
    }
  }
);

// 🎯 NEW: Fetch customers from Customer_tambak where PIC = current user
export const fetchCustomersFromTambakByPIC = createAsyncThunk(
  'customers/fetchCustomersFromTambakByPIC',
  async (params: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const { limit = -1, offset = 0 } = params;
      console.log('═══════════════════════════════════════════════════════');
      console.log('🎯 Redux: Fetching customers from Customer_Tambak where PIC = current user');
      console.log('Parameters:', { limit, offset });
      console.log('═══════════════════════════════════════════════════════');

      // Use the dedicated function for PIC-based filtering
      const customers = await getCustomersFromTambakByPIC({ limit, offset });

      console.log('═══════════════════════════════════════════════════════');
      console.log(`✅ Redux: Successfully fetched ${customers.length} customers from Customer_Tambak by PIC`);
      console.log('═══════════════════════════════════════════════════════');

      return customers.map(transformCustomerData);
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ Redux: Failed to fetch customers from Customer_Tambak by PIC');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customers from Customer_Tambak by PIC');
    }
  }
);

const initialState: CustomersState = {
  customers: [], // For list view - paginated, searchable data
  mapCustomers: [], // For map view - all customers with coordinates
  selectedCustomer: null,
  nearbyCustomers: [],
  isLoading: false, // Deprecated - use granular loading states
  customersLoading: false,
  searchLoading: false,
  nearbyLoading: false,
  detailLoading: false,
  creatingLoading: false,
  loadingMore: false, // New: for infinite scroll loading
  countLoading: false, // New: for customer count loading
  error: null,
  lastUpdated: null,
  customersLastFetched: null, // For TTL caching
  customersCacheTTL: 60 * 60 * 1000, // 1 hour in milliseconds
  customerCount: 0, // New: real-time customer count from database (Customer_Tambak table)
  customerCountLastFetched: null, // New: for customer count caching
  totalCustomerCount: 0, // New: total customer count from Data_Customer table
  totalCustomerCountLoading: false, // New: loading state for total customer count
  totalCustomerCountLastFetched: null, // New: for total customer count caching
  // Map view state
  mapDataLoading: false, // Loading state for all customers data for map view
  allDataLoaded: false, // Flag to indicate if all customer data has been loaded for map
  // Pagination state
  pagination: {
    page: 1,
    limit: 20,
    hasMore: true,
    totalPages: 0
  },
  currentQuery: '', // Track current search query
  // New: Interaction data for sorting
  customerInteractions: [],
  interactionsLoading: false,
  interactionsError: null,
  interactionsLastFetched: null,
  // New: Transaction data for sorting with deduplication tracking
  customerTransactions: [],
  transactionsLoading: false,
  transactionsError: null,
  transactionsLastFetched: null,
  transactionsCacheKey: null, // Track cache key for deduplication
  // New: Schema-compliant transaction sorting state
  schemaTransactionLoading: false,
  schemaTransactionError: null,
  schemaTransactionLastFetched: null,
  lastSchemaSort: null as 'asc' | 'desc' | null,
  // New: Alphabetical sorting state
  alphabeticalSortLoading: false,
  alphabeticalSortError: null,
  alphabeticalSortLastFetched: null,
  lastAlphabeticalSort: null as 'asc' | 'desc' | null,
  // New: Customer type filtering state
  customerTypes: [],
  customerTypesLoading: false,
  customerTypesError: null,
  selectedCustomerType: 'all',
  // Maps search state
  mapsSearchLoading: false,
  mapsSearchResults: [],
  mapsSearchError: null,
  mapsSearchInfo: null,
  // New: Customers with interactions state
  customersWithInteractions: [],
  customersWithInteractionsLoading: false,
  customersWithInteractionsError: null,
  customersWithInteractionsLastFetched: null,
  // New: Customers with completed interactions state
  customersWithCompletedInteractions: [],
  customersWithCompletedInteractionsLoading: false,
  customersWithCompletedInteractionsError: null,
  customersWithCompletedInteractionsLastFetched: null,
  // New: Customers with pending interactions state
  customersWithPendingInteractions: [],
  customersWithPendingInteractionsLoading: false,
  customersWithPendingInteractionsError: null,
  customersWithPendingInteractionsLastFetched: null,
  // New: Customers from Customer_tambak by PIC state
  customersTambakByPIC: [],
  customersTambakByPICLoading: false,
  customersTambakByPICError: null,
  customersTambakByPICLastFetched: null
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    selectCustomer: (state, action: PayloadAction<Customer | null>) => {
      state.selectedCustomer = action.payload;
    },
    
    clearSelectedCustomer: (state) => {
      state.selectedCustomer = null;
    },
    
    clearNearbyCustomers: (state) => {
      state.nearbyCustomers = [];
    },
    
    // Local customer management for offline support
    addCustomerLocal: (state, action: PayloadAction<Customer>) => {
      const existingIndex = state.customers.findIndex(c => c.id === action.payload.id);
      if (existingIndex >= 0) {
        state.customers[existingIndex] = action.payload;
      } else {
        state.customers.push(action.payload);
      }
    },
    
    updateCustomerLocal: (state, action: PayloadAction<Partial<Customer> & { id: string }>) => {
      const index = state.customers.findIndex(c => c.id === action.payload.id);
      if (index >= 0) {
        Object.assign(state.customers[index], action.payload);
      }
      
      // Update selected customer if it matches
      if (state.selectedCustomer && state.selectedCustomer.id === action.payload.id) {
        Object.assign(state.selectedCustomer, action.payload);
      }
    },
    
    // Set selected customer type for filtering
    setSelectedCustomerType: (state, action: PayloadAction<string>) => {
      state.selectedCustomerType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customers limited (for dashboard)
      .addCase(fetchCustomersLimited.pending, (state) => {
        state.customersLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomersLimited.fulfilled, (state, action) => {
        state.customersLoading = false;
        state.customers = action.payload;
        state.customersLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchCustomersLimited.rejected, (state, action) => {
        state.customersLoading = false;
        // Handle cached data case
        const error = action.payload as any;
        if (error?.cached) {
          console.log('Using cached customers data');
        } else {
          state.error = error as string;
        }
      })
      
      // Fetch customers (full)
      .addCase(fetchCustomers.pending, (state) => {
        state.customersLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.customersLoading = false;
        state.customers = action.payload;
        state.customersLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.customersLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch customers improved (Data_Customer as primary source)
      .addCase(fetchCustomersImproved.pending, (state) => {
        state.customersLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomersImproved.fulfilled, (state, action) => {
        state.customersLoading = false;
        state.customers = action.payload;
        state.customersLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchCustomersImproved.rejected, (state, action) => {
        state.customersLoading = false;
        state.error = action.payload as string;
      })
      
      // Search customers (with pagination)
      .addCase(searchCustomers.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchCustomers.fulfilled, (state, action) => {
        state.searchLoading = false;
        const { customers, pagination, append, query } = action.payload;
        
        if (append) {
          // Append new customers for infinite scroll
          state.customers = [...state.customers, ...customers];
        } else {
          // Replace customers for new search
          state.customers = customers;
        }
        
        state.pagination = {
          page: pagination.page,
          limit: pagination.limit,
          hasMore: pagination.hasMore,
          totalPages: 0 // We don't calculate total pages
        };
        state.currentQuery = query;
        state.error = null;
      })
      .addCase(searchCustomers.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload as string;
      })
      
      // Search customers improved (Data_Customer as primary source)
      .addCase(searchCustomersWithImprovedMethod.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchCustomersWithImprovedMethod.fulfilled, (state, action) => {
        state.searchLoading = false;
        const { customers, pagination, append, query } = action.payload;
        
        if (append) {
          // Append new customers for infinite scroll
          state.customers = [...state.customers, ...customers];
        } else {
          // Replace customers for new search
          state.customers = customers;
        }
        
        state.pagination = {
          page: pagination.page,
          limit: pagination.limit,
          hasMore: pagination.hasMore,
          totalPages: 0 // We don't calculate total pages
        };
        state.currentQuery = query;
        state.error = null;
      })
      .addCase(searchCustomersWithImprovedMethod.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload as string;
      })
      
      // COMPREHENSIVE: Search customers for interaction dropdowns (includes ALL customer types)
      .addCase(searchCustomersForInteractionDropdowns.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchCustomersForInteractionDropdowns.fulfilled, (state, action) => {
        state.searchLoading = false;
        const { customers, pagination, append, query } = action.payload;
        
        if (append) {
          // Append new customers for infinite scroll
          state.customers = [...state.customers, ...customers];
        } else {
          // Replace customers for new search
          state.customers = customers;
        }
        
        state.pagination = {
          currentPage: pagination.page,
          hasMore: pagination.hasMore,
          totalPages: 0 // We don't calculate total pages
        };
        state.currentQuery = query;
        state.error = null;
        console.log(`✅ Redux: Comprehensive interaction search completed with ${customers.length} customers`);
      })
      .addCase(searchCustomersForInteractionDropdowns.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload as string;
        console.error('❌ Redux: Comprehensive interaction search failed:', state.error);
      })
      
      // Load more customers (infinite scroll)
      .addCase(loadMoreCustomers.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreCustomers.fulfilled, (state, action) => {
        state.loadingMore = false;
        const { customers, pagination } = action.payload;
        
        // Append new customers
        state.customers = [...state.customers, ...customers];
        state.pagination = {
          page: pagination.page,
          limit: pagination.limit,
          hasMore: pagination.hasMore,
          totalPages: 0
        };
        state.error = null;
      })
      .addCase(loadMoreCustomers.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.payload as string;
      })
      
      // Fetch nearby customers
      .addCase(fetchNearbyCustomers.pending, (state) => {
        state.nearbyLoading = true;
        state.error = null;
      })
      .addCase(fetchNearbyCustomers.fulfilled, (state, action) => {
        state.nearbyLoading = false;
        state.nearbyCustomers = action.payload;
        state.error = null;
      })
      .addCase(fetchNearbyCustomers.rejected, (state, action) => {
        state.nearbyLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch customer by ID
      .addCase(fetchCustomerById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.detailLoading = false;
        
        // Update customer in list if it exists
        const index = state.customers.findIndex(c => c.id === action.payload.id);
        if (index >= 0) {
          state.customers[index] = action.payload;
        } else {
          state.customers.push(action.payload);
        }
        
        // Update selected customer if it matches
        if (state.selectedCustomer && state.selectedCustomer.id === action.payload.id) {
          state.selectedCustomer = action.payload;
        }
        
        state.error = null;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch customer stats
      .addCase(fetchCustomerStats.pending, (state) => {
        state.customersLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerStats.fulfilled, (state, _action) => {
        state.customersLoading = false;
        // Stats are typically used at component level, not stored in slice
        state.error = null;
      })
      .addCase(fetchCustomerStats.rejected, (state, action) => {
        state.customersLoading = false;
        state.error = action.payload as string;
      })
      
      // Create customer
      .addCase(createCustomer.pending, (state) => {
        state.creatingLoading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.creatingLoading = false;
        state.customers.unshift(action.payload); // Add to beginning of list
        state.customerCount += 1; // Increment count when new customer is created
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.creatingLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch customer count
      .addCase(fetchCustomerCount.pending, (state) => {
        state.countLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerCount.fulfilled, (state, action) => {
        state.countLoading = false;
        state.customerCount = action.payload.total_customers;
        state.customerCountLastFetched = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchCustomerCount.rejected, (state, action) => {
        state.countLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch total customer count from Data_Customer table
      .addCase(fetchTotalCustomerCount.pending, (state) => {
        state.totalCustomerCountLoading = true;
        state.error = null;
      })
      .addCase(fetchTotalCustomerCount.fulfilled, (state, action) => {
        state.totalCustomerCountLoading = false;
        state.totalCustomerCount = action.payload.total_customers;
        state.totalCustomerCountLastFetched = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchTotalCustomerCount.rejected, (state, action) => {
        state.totalCustomerCountLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch customer interactions for sorting
      .addCase(fetchCustomerInteractions.pending, (state) => {
        state.interactionsLoading = true;
        state.interactionsError = null;
      })
      .addCase(fetchCustomerInteractions.fulfilled, (state, action) => {
        state.interactionsLoading = false;
        state.customerInteractions = action.payload;
        state.interactionsLastFetched = getWIBTimeForDirectus();
        state.interactionsError = null;
      })
      .addCase(fetchCustomerInteractions.rejected, (state, action) => {
        state.interactionsLoading = false;
        state.interactionsError = action.payload as string;
        // Don't block functionality if interactions fail
        console.warn('Customer interactions fetch failed, sorting by interaction will be disabled');
      })
      
      // Fetch customer transactions for sorting
      .addCase(fetchCustomerTransactions.pending, (state) => {
        state.transactionsLoading = true;
        state.transactionsError = null;
      })
      .addCase(fetchCustomerTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        state.customerTransactions = action.payload;
        state.transactionsLastFetched = getWIBTimeForDirectus();
        state.transactionsCacheKey = `tx_${Date.now()}`;
        state.transactionsError = null;
      })
      .addCase(fetchCustomerTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.transactionsError = action.payload as string;
        // Don't block functionality if transactions fail
        console.warn('Customer transactions fetch failed, sorting by transaction will be disabled');
      })
      
      // Fetch customers by transaction schema (schema-compliant sorting)
      .addCase(fetchCustomersByTransactionSchema.pending, (state) => {
        state.schemaTransactionLoading = true;
        state.schemaTransactionError = null;
      })
      .addCase(fetchCustomersByTransactionSchema.fulfilled, (state, action) => {
        state.schemaTransactionLoading = false;
        state.customers = action.payload.customers;
        state.lastSchemaSort = action.payload.sortOrder;
        state.schemaTransactionLastFetched = getWIBTimeForDirectus();
        state.customersLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.schemaTransactionError = null;
        state.error = null;
      })
      .addCase(fetchCustomersByTransactionSchema.rejected, (state, action) => {
        state.schemaTransactionLoading = false;
        state.schemaTransactionError = action.payload as string;
        // Don't block functionality if schema-compliant sorting fails
        console.warn('Schema-compliant transaction sorting failed, fallback to regular sorting may be needed');
      })
      
      // Fetch customers alphabetically (Data_Customer-based sorting)
      .addCase(fetchCustomersAlphabetically.pending, (state) => {
        state.alphabeticalSortLoading = true;
        state.alphabeticalSortError = null;
      })
      .addCase(fetchCustomersAlphabetically.fulfilled, (state, action) => {
        state.alphabeticalSortLoading = false;
        state.customers = action.payload.customers;
        state.lastAlphabeticalSort = action.payload.sortOrder;
        state.alphabeticalSortLastFetched = getWIBTimeForDirectus();
        state.customersLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.alphabeticalSortError = null;
        state.error = null;
      })
      .addCase(fetchCustomersAlphabetically.rejected, (state, action) => {
        state.alphabeticalSortLoading = false;
        state.alphabeticalSortError = action.payload as string;
        // Don't block functionality if alphabetical sorting fails
        console.warn('Data_Customer alphabetical sorting failed, attempting alternative server-side methods');
      })
      
      // Fetch all customers for map view (no pagination limits)
      .addCase(fetchAllCustomersForMap.pending, (state) => {
        state.mapDataLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCustomersForMap.fulfilled, (state, action) => {
        state.mapDataLoading = false;
        state.mapCustomers = action.payload.customers; // Use separate mapCustomers array
        state.allDataLoaded = true;
        state.customersLastFetched = getWIBTimeForDirectus();
        state.lastUpdated = getWIBTimeForDirectus();
        state.error = null;
        console.log('🗺️ All customers loaded for map view:', state.mapCustomers.length);
      })
      .addCase(fetchAllCustomersForMap.rejected, (state, action) => {
        state.mapDataLoading = false;
        state.allDataLoaded = false;
        state.error = action.payload as string;
        console.error('❌ Failed to load all customers for map view');
      })
      
      // Fetch unique customer types
      .addCase(fetchUniqueCustomerTypes.pending, (state) => {
        state.customerTypesLoading = true;
        state.customerTypesError = null;
      })
      .addCase(fetchUniqueCustomerTypes.fulfilled, (state, action) => {
        state.customerTypesLoading = false;
        state.customerTypes = action.payload;
        state.customerTypesError = null;
        console.log('🏷️ Customer types loaded successfully:', action.payload.length);
      })
      .addCase(fetchUniqueCustomerTypes.rejected, (state, action) => {
        state.customerTypesLoading = false;
        state.customerTypesError = action.payload as string;
        console.error('❌ Failed to load customer types');
      })
      
      // Maps search actions
      .addCase(searchCustomersForMaps.pending, (state) => {
        state.mapsSearchLoading = true;
        state.mapsSearchError = null;
      })
      .addCase(searchCustomersForMaps.fulfilled, (state, action) => {
        state.mapsSearchLoading = false;
        state.mapsSearchResults = action.payload.data;
        state.mapsSearchInfo = {
          query: action.payload.searchInfo.query,
          customerType: action.payload.searchInfo.customerType,
          totalFound: action.payload.totalFound,
          mapReady: action.payload.mapReady,
          hasPhoneMatches: action.payload.hasPhoneMatches
        };
        state.mapsSearchError = null;
        console.log('🗺️ Maps search completed successfully:', {
          results: action.payload.data.length,
          totalFound: action.payload.totalFound,
          phoneMatches: action.payload.hasPhoneMatches
        });
      })
      .addCase(searchCustomersForMaps.rejected, (state, action) => {
        state.mapsSearchLoading = false;
        state.mapsSearchError = action.payload as string;
        console.error('❌ Maps search failed');
      })

      // Fetch customers with interactions for current user
      .addCase(fetchCustomersWithInteractions.pending, (state) => {
        state.customersWithInteractionsLoading = true;
        state.customersWithInteractionsError = null;
      })
      .addCase(fetchCustomersWithInteractions.fulfilled, (state, action) => {
        state.customersWithInteractionsLoading = false;
        state.customersWithInteractions = action.payload;
        state.customersWithInteractionsLastFetched = getWIBTimeForDirectus();
        state.customersWithInteractionsError = null;
        console.log('✅ Customers with interactions loaded:', action.payload.length);
      })
      .addCase(fetchCustomersWithInteractions.rejected, (state, action) => {
        state.customersWithInteractionsLoading = false;
        state.customersWithInteractionsError = action.payload as string;
        console.error('❌ Failed to load customers with interactions');
      })

      // ✅ Fetch customers with COMPLETED interactions (done = true)
      .addCase(fetchCustomersWithCompletedInteractions.pending, (state) => {
        state.customersWithCompletedInteractionsLoading = true;
        state.customersWithCompletedInteractionsError = null;
      })
      .addCase(fetchCustomersWithCompletedInteractions.fulfilled, (state, action) => {
        state.customersWithCompletedInteractionsLoading = false;
        state.customersWithCompletedInteractions = action.payload;
        state.customersWithCompletedInteractionsLastFetched = getWIBTimeForDirectus();
        state.customersWithCompletedInteractionsError = null;
        console.log('✅ Customers with COMPLETED interactions loaded:', action.payload.length);
      })
      .addCase(fetchCustomersWithCompletedInteractions.rejected, (state, action) => {
        state.customersWithCompletedInteractionsLoading = false;
        state.customersWithCompletedInteractionsError = action.payload as string;
        console.error('❌ Failed to load customers with COMPLETED interactions');
      })

      // ⏳ Fetch customers with PENDING interactions (done = false)
      .addCase(fetchCustomersWithPendingInteractions.pending, (state) => {
        state.customersWithPendingInteractionsLoading = true;
        state.customersWithPendingInteractionsError = null;
      })
      .addCase(fetchCustomersWithPendingInteractions.fulfilled, (state, action) => {
        state.customersWithPendingInteractionsLoading = false;
        state.customersWithPendingInteractions = action.payload;
        state.customersWithPendingInteractionsLastFetched = getWIBTimeForDirectus();
        state.customersWithPendingInteractionsError = null;
        console.log('✅ Customers with PENDING interactions loaded:', action.payload.length);
      })
      .addCase(fetchCustomersWithPendingInteractions.rejected, (state, action) => {
        state.customersWithPendingInteractionsLoading = false;
        state.customersWithPendingInteractionsError = action.payload as string;
        console.error('❌ Failed to load customers with PENDING interactions');
      })

      // Fetch customers from Customer_tambak by PIC
      .addCase(fetchCustomersFromTambakByPIC.pending, (state) => {
        state.customersTambakByPICLoading = true;
        state.customersTambakByPICError = null;
      })
      .addCase(fetchCustomersFromTambakByPIC.fulfilled, (state, action) => {
        state.customersTambakByPICLoading = false;
        state.customersTambakByPIC = action.payload;
        state.customersTambakByPICLastFetched = getWIBTimeForDirectus();
        state.customersTambakByPICError = null;
        console.log('✅ Customers from Customer_tambak by PIC loaded:', action.payload.length);
      })
      .addCase(fetchCustomersFromTambakByPIC.rejected, (state, action) => {
        state.customersTambakByPICLoading = false;
        state.customersTambakByPICError = action.payload as string;
        console.error('❌ Failed to load customers from Customer_tambak by PIC');
      });
  },
});

export const {
  clearError,
  selectCustomer,
  clearSelectedCustomer,
  clearNearbyCustomers,
  addCustomerLocal,
  updateCustomerLocal,
  setSelectedCustomerType,
} = customersSlice.actions;

// Note: loadMoreCustomers is already exported as a createAsyncThunk above

// Selectors
export const selectCustomersState = (state: { customers: CustomersState }) => state.customers;
export const selectCustomersList = (state: { customers: CustomersState }) => state.customers.customers;
export const selectMapCustomersList = (state: { customers: CustomersState }) => state.customers.mapCustomers;
export const selectSelectedCustomer = (state: { customers: CustomersState }) => state.customers.selectedCustomer;
export const selectNearbyCustomers = (state: { customers: CustomersState }) => state.customers.nearbyCustomers;
export const selectCustomersLoading = (state: { customers: CustomersState }) => state.customers.isLoading; // Deprecated
export const selectCustomersError = (state: { customers: CustomersState }) => state.customers.error;
export const selectCustomersLastUpdated = (state: { customers: CustomersState }) => state.customers.lastUpdated;

// Granular loading selectors
export const selectCustomersDataLoading = (state: { customers: CustomersState }) => state.customers.customersLoading;
export const selectSearchLoading = (state: { customers: CustomersState }) => state.customers.searchLoading;
export const selectNearbyLoading = (state: { customers: CustomersState }) => state.customers.nearbyLoading;
export const selectDetailLoading = (state: { customers: CustomersState }) => state.customers.detailLoading;
export const selectCreatingLoading = (state: { customers: CustomersState }) => state.customers.creatingLoading;
export const selectLoadingMore = (state: { customers: CustomersState }) => state.customers.loadingMore;

// Pagination selectors
export const selectPagination = (state: { customers: CustomersState }) => state.customers.pagination;
export const selectHasMore = (state: { customers: CustomersState }) => state.customers.pagination.hasMore;
export const selectCurrentPage = (state: { customers: CustomersState }) => state.customers.pagination.page;
export const selectCurrentQuery = (state: { customers: CustomersState }) => state.customers.currentQuery;

// Combined loading selector for Dashboard
export const selectCriticalCustomerDataLoading = (state: { customers: CustomersState }) => 
  state.customers.customersLoading;

// Cache status selector
export const selectCustomersNeedRefresh = (state: { customers: CustomersState }) => {
  const { customersLastFetched, customersCacheTTL } = state.customers;
  if (!customersLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(customersLastFetched).getTime();
  // Handle invalid dates
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= customersCacheTTL;
};

// Helper selectors
export const selectCustomerById = (state: { customers: CustomersState }, customerId: string) => 
  state.customers.customers.find(customer => customer.id === customerId);

export const selectCustomersByPriority = (state: { customers: CustomersState }, priority: Customer['priority']) => 
  state.customers.customers.filter(customer => customer.priority === priority);

export const selectActiveCustomers = (state: { customers: CustomersState }) => 
  state.customers.customers.filter(customer => customer.status === 'active');

export const selectHighPriorityCustomers = (state: { customers: CustomersState }) => 
  state.customers.customers.filter(customer => customer.priority === 'high');

export const selectCustomersCount = (state: { customers: CustomersState }) => 
  state.customers.customers.length;

export const selectCustomerCount = (state: { customers: CustomersState }) => 
  state.customers.customerCount;

export const selectCustomerCountLoading = (state: { customers: CustomersState }) => 
  state.customers.countLoading;

export const selectTotalCustomerCount = (state: { customers: CustomersState }) => 
  state.customers.totalCustomerCount;

export const selectTotalCustomerCountLoading = (state: { customers: CustomersState }) => 
  state.customers.totalCustomerCountLoading;

// Interaction data selectors for sorting
export const selectCustomerInteractions = (state: { customers: CustomersState }) => 
  state.customers.customerInteractions;

export const selectInteractionsLoading = (state: { customers: CustomersState }) => 
  state.customers.interactionsLoading;

export const selectInteractionsError = (state: { customers: CustomersState }) => 
  state.customers.interactionsError;

export const selectHasInteractionData = (state: { customers: CustomersState }) => 
  state.customers.customerInteractions && state.customers.customerInteractions.length > 0;

// Transaction data selectors for sorting
export const selectCustomerTransactions = (state: { customers: CustomersState }) => 
  state.customers.customerTransactions;

export const selectTransactionsLoading = (state: { customers: CustomersState }) => 
  state.customers.transactionsLoading;

export const selectTransactionsError = (state: { customers: CustomersState }) => 
  state.customers.transactionsError;

export const selectHasTransactionData = (state: { customers: CustomersState }) => 
  state.customers.customerTransactions && state.customers.customerTransactions.length > 0;

// Schema transaction selectors
export const selectSchemaTransactionLoading = (state: { customers: CustomersState }) => 
  state.customers.schemaTransactionLoading;

export const selectSchemaTransactionError = (state: { customers: CustomersState }) => 
  state.customers.schemaTransactionError;

export const selectLastSchemaSort = (state: { customers: CustomersState }) => 
  state.customers.lastSchemaSort;

// Alphabetical sorting selectors
export const selectAlphabeticalSortLoading = (state: { customers: CustomersState }) => 
  state.customers.alphabeticalSortLoading;

export const selectAlphabeticalSortError = (state: { customers: CustomersState }) => 
  state.customers.alphabeticalSortError;

export const selectLastAlphabeticalSort = (state: { customers: CustomersState }) => 
  state.customers.lastAlphabeticalSort;

// Map view selectors
export const selectMapDataLoading = (state: { customers: CustomersState }) => 
  state.customers.mapDataLoading;

export const selectAllDataLoaded = (state: { customers: CustomersState }) => 
  state.customers.allDataLoaded;

// Customer type filter selectors
export const selectCustomerTypes = (state: { customers: CustomersState }) => 
  state.customers.customerTypes;

export const selectCustomerTypesLoading = (state: { customers: CustomersState }) => 
  state.customers.customerTypesLoading;

export const selectCustomerTypesError = (state: { customers: CustomersState }) => 
  state.customers.customerTypesError;

export const selectSelectedCustomerType = (state: { customers: CustomersState }) => 
  state.customers.selectedCustomerType;

// Maps search selectors
export const selectMapsSearchLoading = (state: { customers: CustomersState }) => 
  state.customers.mapsSearchLoading;

export const selectMapsSearchResults = (state: { customers: CustomersState }) => 
  state.customers.mapsSearchResults;

export const selectMapsSearchError = (state: { customers: CustomersState }) => 
  state.customers.mapsSearchError;

export const selectMapsSearchInfo = (state: { customers: CustomersState }) =>
  state.customers.mapsSearchInfo;

// Customers with interactions selectors
export const selectCustomersWithInteractions = (state: { customers: CustomersState }) =>
  state.customers.customersWithInteractions;

export const selectCustomersWithInteractionsLoading = (state: { customers: CustomersState }) =>
  state.customers.customersWithInteractionsLoading;

export const selectCustomersWithInteractionsError = (state: { customers: CustomersState }) =>
  state.customers.customersWithInteractionsError;

export const selectCustomersWithInteractionsCount = (state: { customers: CustomersState }) =>
  state.customers.customersWithInteractions.length;

// ✅ Selectors for COMPLETED interactions (done = true)
export const selectCustomersWithCompletedInteractions = (state: { customers: CustomersState }) =>
  state.customers.customersWithCompletedInteractions;

export const selectCustomersWithCompletedInteractionsLoading = (state: { customers: CustomersState }) =>
  state.customers.customersWithCompletedInteractionsLoading;

export const selectCustomersWithCompletedInteractionsError = (state: { customers: CustomersState }) =>
  state.customers.customersWithCompletedInteractionsError;

export const selectCustomersWithCompletedInteractionsCount = (state: { customers: CustomersState }) =>
  state.customers.customersWithCompletedInteractions?.length || 0;

// ⏳ Selectors for PENDING interactions (done = false)
export const selectCustomersWithPendingInteractions = (state: { customers: CustomersState }) =>
  state.customers.customersWithPendingInteractions;

export const selectCustomersWithPendingInteractionsLoading = (state: { customers: CustomersState }) =>
  state.customers.customersWithPendingInteractionsLoading;

export const selectCustomersWithPendingInteractionsError = (state: { customers: CustomersState }) =>
  state.customers.customersWithPendingInteractionsError;

export const selectCustomersWithPendingInteractionsCount = (state: { customers: CustomersState }) =>
  state.customers.customersWithPendingInteractions?.length || 0;

// Customers from Customer_tambak by PIC selectors
export const selectCustomersTambakByPIC = (state: { customers: CustomersState }) =>
  state.customers.customersTambakByPIC;

export const selectCustomersTambakByPICLoading = (state: { customers: CustomersState }) =>
  state.customers.customersTambakByPICLoading;

export const selectCustomersTambakByPICError = (state: { customers: CustomersState }) =>
  state.customers.customersTambakByPICError;

export const selectCustomersTambakByPICCount = (state: { customers: CustomersState }) =>
  state.customers.customersTambakByPIC.length;

// Search and filter utilities
export const selectFilteredCustomers = (state: { customers: CustomersState }, searchQuery: string) => {
  if (!searchQuery || !searchQuery.trim()) {
    return state.customers.customers;
  }
  
  const query = searchQuery.toLowerCase();
  return state.customers.customers.filter(customer => 
    customer.name?.toLowerCase().includes(query) ||
    customer.address?.toLowerCase().includes(query) ||
    customer.contact_person?.toLowerCase().includes(query) ||
    customer.phone?.toLowerCase().includes(query)
  );
};

// Enhanced sorting function supporting new sort criteria
export const selectCustomersWithSort = (
  state: { customers: CustomersState }, 
  sortCriteria: 'name' | 'last_interaction' | 'last_transaction' | 'total_transaction',
  sortOrder: 'asc' | 'desc' = 'asc',
  interactionData?: any[], // Pass interaction data from interactions slice
  transactionData?: any[]  // Pass transaction data for transaction sorting
) => {
  const customers = [...state.customers.customers];
  
  // Special handling for last_transaction sorting to ensure customers without transactions go to end
  if (sortCriteria === 'last_transaction') {
    // Separate customers with and without transactions
    const customersWithTransactions: typeof customers = [];
    const customersWithoutTransactions: typeof customers = [];
    
    customers.forEach(customer => {
      const hasTransaction = transactionData?.some(
        (transaction: any) => transaction.NoPelanggan === customer.idcust
      );
      
      if (hasTransaction) {
        customersWithTransactions.push(customer);
      } else {
        customersWithoutTransactions.push(customer);
      }
    });
    
    // Sort customers with transactions by transaction date
    customersWithTransactions.sort((a, b) => {
      const aTransaction = transactionData?.find(
        (transaction: any) => transaction.NoPelanggan === a.idcust
      );
      const bTransaction = transactionData?.find(
        (transaction: any) => transaction.NoPelanggan === b.idcust
      );
      
      const aDate = aTransaction?.TglFaktur ? new Date(aTransaction.TglFaktur).getTime() : 0;
      const bDate = bTransaction?.TglFaktur ? new Date(bTransaction.TglFaktur).getTime() : 0;
      
      const comparison = aDate - bDate; // Ascending order
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    
    // Combine: customers with transactions first, then customers without transactions
    return [...customersWithTransactions, ...customersWithoutTransactions];
  }
  
  // Standard sorting for other criteria
  return customers.sort((a, b) => {
    let comparison = 0;
    
    switch (sortCriteria) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
        
      case 'last_interaction':
        // Use interaction data if available, otherwise fall back to customer's last interaction
        let aLastInteraction: string | undefined;
        let bLastInteraction: string | undefined;
        
        if (interactionData && interactionData.length > 0) {
          // ✅ DEBUG: Interaction data is already filtered by current user at fetch time
          // Find most recent interaction for each customer
          const aInteractions = interactionData.filter(
            (interaction: any) => interaction.No_pelanggan === a.idcust
          );
          const bInteractions = interactionData.filter(
            (interaction: any) => interaction.No_pelanggan === b.idcust
          );
          
          aLastInteraction = aInteractions.length > 0 
            ? aInteractions.sort((x: any, y: any) => 
                new Date(y.date_created).getTime() - new Date(x.date_created).getTime()
              )[0].date_created 
            : undefined;
            
          bLastInteraction = bInteractions.length > 0 
            ? bInteractions.sort((x: any, y: any) => 
                new Date(y.date_created).getTime() - new Date(x.date_created).getTime()
              )[0].date_created 
            : undefined;
        } else {
          // Fallback to customer's last interaction field
          aLastInteraction = a.last_visit || a.lastInteraction;
          bLastInteraction = b.last_visit || b.lastInteraction;
        }
        
        if (!aLastInteraction && !bLastInteraction) comparison = 0;
        else if (!aLastInteraction) comparison = 1; // No interaction goes to end
        else if (!bLastInteraction) comparison = -1;
        else {
          comparison = new Date(aLastInteraction).getTime() - new Date(bLastInteraction).getTime();
        }
        break;
      
        
      case 'total_transaction':
        // Use totalTransaksi from Data_Customer or fall back to revenue extraction
        // Note: totalTransaksi should be available in the customer data from Directus
        const aTransaction = (a as any).totalTransaksi || extractTransactionAmount(a.revenue) || 0;
        const bTransaction = (b as any).totalTransaksi || extractTransactionAmount(b.revenue) || 0;
        comparison = Number(aTransaction) - Number(bTransaction);
        break;
        
      default:
        comparison = 0;
    }
    
    // Apply sort order
    return sortOrder === 'desc' ? -comparison : comparison;
  });
};

// Helper function to extract numeric transaction amount from revenue string
const extractTransactionAmount = (revenue?: string): number => {
  if (!revenue) return 0;
  
  // Remove currency symbols and formatting, extract number
  const numericString = revenue.replace(/[^\d.,]/g, '');
  const cleanNumber = numericString.replace(/,/g, '');
  const amount = parseFloat(cleanNumber);
  
  return isNaN(amount) ? 0 : amount;
};

export const selectSortedCustomers = (state: { customers: CustomersState }, sortBy: 'name' | 'priority' | 'last_visit') => {
  const customers = [...state.customers.customers];
  
  return customers.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'priority':
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      case 'last_visit':
        if (!a.last_visit && !b.last_visit) return 0;
        if (!a.last_visit) return 1;
        if (!b.last_visit) return -1;
        return new Date(b.last_visit).getTime() - new Date(a.last_visit).getTime();
      default:
        return 0;
    }
  });
};

export default customersSlice.reducer;